import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Bar } from 'react-chartjs-2';
import SlideLoading from './SlideLoading';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useSelector } from 'react-redux';
import { getLeadershipTeams, getSelectedAssessmentPeriod } from 'common/selectors/auth';
import userContextSlice from '@/common/slices/userContextSlice';
import { useWelcomeTourData } from '../useWelcomeTourData';
import { SlideContainer, EmptyState, HeroBanner, BannerDecoClip, HeroLabel, HeroHeadline, HeroBody } from './slideStyles';
import { computeChartData } from 'areas/culture-hub/components/interventions/recommendations/ScoresChart';
import { getChartTooltipConfig } from '@/common/components/charts/chartTooltipConfig';

const ContentContainer = styled.div`
  width: min(100%, 1140px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderPanel = styled.div`
  width: 100%;
  padding: .75rem 1.75rem 0rem;
  box-sizing: border-box;

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 0.5rem 0.65rem 0;
  }
`;

const ExpandedSlideContainer = styled(SlideContainer)`
  height: 100%;
  flex: 1;
  min-height: 0;
`;

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Plain-English, one-line explanation shown after each pillar name in the narrative.
const PILLAR_BLURBS: Record<string, string> = {
  inclusion: 'Everyone feels valued, respected, and able to fully contribute',
  advancement: 'Members have clear, fair paths to grow and move into greater roles',
  compensation: 'Pay, benefits, and rewards feel fair and transparent',
  representation: 'Diverse voices are visible and heard at every level',
  'psychological safety': 'People feel safe to speak up, ask questions, and take risks',
  'emotional intelligence': 'Empathy, self-awareness, and healthy communication shape how the team works',
};

const pillarBlurb = (name: string): string =>
  PILLAR_BLURBS[name.toLowerCase()] || '';

const HoverLabel = styled.span`
  border-bottom: 1px  #6b7280;
  cursor: help;
  &:hover {
    border-bottom: 1px dotted #6b7280;
    cursor: help;
  }
  `;

const ChartWrapper = styled.div`
  min-height: 260px;
  height: clamp(260px, 45vh, 480px);
  width: 100%;
  margin: 0 auto;
  position: relative;
  background: #ffffff;
  border-radius: 0.75rem;
  padding: 0rem 1.75rem .75rem;
  @media (max-width: 900px) {
    min-height: 280px;
    height: clamp(280px, 35vh, 360px);
    padding: 0.85rem 0.85rem;
  }
`;

const NarrativeBox = styled.div`
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 0.75rem;
  padding: 1.25rem 1.1rem;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #374151;
  line-height: 1.65;
  text-align: center;
`;

const Strong = styled.strong<{ $color?: string }>`
  font-weight: 700;
  color: ${props => props.$color || 'inherit'};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  padding-bottom: 1.25rem;
`;



const Slide6OrganizationalComparison: React.FC = () => {
  const {
    sortedPillarsByScore,
    assessmentPeriodId,
    teamId,
    isLoading,
  } = useWelcomeTourData();

  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const leadershipTeams = useSelector(getLeadershipTeams);
  const selectedTeamId = useSelector(
    userContextSlice.selectors.getSelectedTeamId as any
  ) as number | undefined;
  const teamName = leadershipTeams?.find((team) => team.id === selectedTeamId)?.name || 'Team';
  const hasPillars = sortedPillarsByScore.length > 0;

  const renderPillarName = (name: string, color: string) => {
    const blurb = pillarBlurb(name);
    return (
      <HoverLabel title={blurb}>
        <Strong $color={color}>{name}</Strong>
      </HoverLabel>
    );
  };

  const [showDataLabels, setShowDataLabels] = useState(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const updateDataLabels = () => setShowDataLabels(!mediaQuery.matches);

    updateDataLabels();
    mediaQuery.addEventListener('change', updateDataLabels);
    return () => mediaQuery.removeEventListener('change', updateDataLabels);
  }, []);

  const { chartData, deltas } = useMemo(() => {
    const result = computeChartData(sortedPillarsByScore, teamName, { teamDataLabelMode: 'score' });
    return {
      chartData: {
        labels: result.labels,
        datasets: result.datasets.map(dataset => ({
          ...dataset,
          datalabels: {
            ...dataset.datalabels,
            display: showDataLabels,
          },
        })),
      },
      deltas: result.deltas,
    };
  }, [sortedPillarsByScore, teamName, showDataLabels]);

  const narrative = useMemo(() => {
    if (!hasPillars) return null;
    const withDelta = sortedPillarsByScore.map(p => ({ name: p.name, delta: p.score - p.org_avg }));
    const above = withDelta.filter(p => p.delta > 0.2).sort((a, b) => b.delta - a.delta);
    const below = withDelta.filter(p => p.delta < -0.2).sort((a, b) => a.delta - b.delta);
    return { above, below };
  }, [sortedPillarsByScore, hasPillars]);

  const chartKey = useMemo(() => {
    const dataHash = sortedPillarsByScore.map(p => `${p.name}:${p.score}:${p.org_avg}`).join('|');
    return `slide3-chart-${assessmentPeriodId || 'no-period'}-${teamId || 'no-team'}-${dataHash}`;
  }, [sortedPillarsByScore, teamId, assessmentPeriodId]);

  const options = useMemo(() => {
    const abbreviateLabel = (label: string) =>
      label
        .split(' ')
        .map(word => `${word.slice(0, 3)}.`)
        .join(' ');

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            font: { size: 11, family: 'Poppins, sans-serif' },
            padding: 12,
            usePointStyle: true,
          },
        },
        tooltip: {
          ...getChartTooltipConfig(),
          callbacks: {
            afterLabel: (context: any) => {
              const delta = deltas[context.dataIndex];
              if (delta !== undefined && !isNaN(delta)) {
                const deltaStr = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
                const direction = delta > 0 ? '(above org)' : delta < 0 ? '(below org)' : '(same as org)';
                return `Delta: ${deltaStr} ${direction}`;
              }
              return '';
            },
          },
        },
        datalabels: { display: showDataLabels },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 10, family: 'Poppins, sans-serif' },
            color: '#4b5563',
            maxRotation: showDataLabels ? 45 : 90,
            minRotation: showDataLabels ? 45 : 90,
            align: showDataLabels ? 'center' as const : 'start' as const,
            callback: (value: any, index: number) => {
              const label = Array.isArray(chartData.labels)
                ? String(chartData.labels[index] ?? value)
                : String(value);
              return showDataLabels ? label : abbreviateLabel(label);
            },
          },
          grid: { display: false },
        },
        y: {
          min: 0,
          max: 10,
          ticks: {
            font: { size: 11, family: 'Poppins, sans-serif' },
            color: '#4b5563',
            stepSize: 2,
          },
          grid: { color: '#e5e7eb' },
        },
      },
      layout: { padding: { top: 20 } },
    };
  }, [chartData.labels, deltas, showDataLabels]);

  return (
    <ExpandedSlideContainer>
      <HeroBanner>
        <BannerDecoClip />
        <ContentContainer>
          <HeaderPanel>
            <HeroLabel>Org. Comparison - { selectedAssessmentPeriod?.name || 'Current Assessment Period' }</HeroLabel>
            <HeaderRow>
              <div>
                <HeroHeadline>Here is how your team compares</HeroHeadline>
                <HeroBody>Your team's scores compared to organization averages.</HeroBody>
              </div>
            </HeaderRow>
          </HeaderPanel>

          {isLoading && <SlideLoading message="Loading team results..." />}
          {!isLoading && !hasPillars && <EmptyState>No pillar data available yet.</EmptyState>}
          {!isLoading && hasPillars && (
            <ChartWrapper>
              <Bar
                key={chartKey}
                data={chartData}
                options={options}
                plugins={[ChartDataLabels]}
                redraw={true}
              />
            </ChartWrapper>
          )}

          {!isLoading && narrative && (
            <NarrativeBox>
            {narrative.above.length > 0 && (
              <span>
                {teamName} leads the organization in{' '}
                {narrative.above.slice(0, 2).map((p, i, arr) => (
                  <span key={p.name}>
                    {renderPillarName(p.name, '#16A34A')}
                    {' '}
                    <Strong $color="#16A34A">(+{p.delta.toFixed(1)})</Strong>
                    {i < arr.length - 1 ? '; and ' : ''}
                  </span>
                ))}.
                {narrative.below.length > 0 ? ' ' : ''}
              </span>
            )}
            {narrative.below.length > 0 && (
              <div style={{ marginTop: narrative.above.length > 0 ? '0.5rem' : undefined }}>
                {narrative.above.length === 0 ? `${teamName} trails` : 'Scores trail'} organizational averages in{' '}
                {narrative.below.slice(0, 2).map((p, i, arr) => (
                  <span key={p.name}>
                    {renderPillarName(p.name, '#cf870b')}
                    {' '}
                    <Strong $color="#cf870b">({p.delta.toFixed(1)})</Strong>
                    {i < arr.length - 1 ? '; and ' : ''}
                  </span>
                ))}
                {' '}— {narrative.below.length === 1 ? 'this is' : 'these are'} your biggest growth opportunity.
              </div>
            )}
            {narrative.above.length === 0 && narrative.below.length === 0 && (
              <span>{teamName} is performing close to the organization average across all areas.</span>
            )}
          </NarrativeBox>
        )}

        </ContentContainer>
      </HeroBanner>

    </ExpandedSlideContainer>
  );
};

export default Slide6OrganizationalComparison;